/**
 * Phase 6b — top-level Sentry error boundary + friendly fallback.
 *
 * Wraps the React tree so a thrown render-time error (a) gets
 * reported to Sentry via `Sentry.ErrorBoundary` and (b) the user
 * sees an actionable recovery surface instead of a blank page.
 *
 * The fallback is intentionally minimal — applicants who hit a
 * rendering crash mid-form need to know we know, and that
 * refreshing might unstick them. We don't surface the raw error
 * message because it would either leak internal detail or be
 * unintelligible to a non-engineer.
 *
 * Source-cited Sentry APIs (verified against
 * `node_modules/@sentry/react/build/types/errorboundary.d.ts`):
 *   - `<ErrorBoundary fallback={...}>` — wraps children, catches
 *     render-time errors, reports to Sentry, renders the fallback.
 *   - `FallbackRender` — `(errorData) => ReactElement` signature
 *     used when the fallback needs the error / resetError handle.
 */

import { ErrorBoundary, type FallbackRender } from '@sentry/react';
import type { ReactNode } from 'react';

/**
 * Props for the fallback component. Mirrors the data Sentry passes
 * to `FallbackRender` so the same component works whether it's
 * rendered standalone (tests) or inside the boundary.
 */
export interface ISentryErrorFallbackProps {
  error: unknown;
  componentStack: string;
  eventId: string;
  resetError: () => void;
}

/**
 * Friendly fallback. Inline styles only — keeps this surface
 * working even if the global stylesheet failed to load (which is
 * a plausible cause of the boundary firing in the first place).
 */
export function SentryErrorFallback(
  _props: ISentryErrorFallbackProps,
): JSX.Element {
  return (
    <div
      role="alert"
      style={{
        margin: '4rem auto',
        maxWidth: '32rem',
        padding: '2rem',
        textAlign: 'center',
        fontFamily: 'system-ui, sans-serif',
      }}
    >
      <h1 style={{ fontSize: '1.5rem', marginBottom: '0.75rem' }}>
        Something went wrong
      </h1>
      <p style={{ marginBottom: '1.5rem', lineHeight: 1.5 }}>
        We've been notified and are looking into it. Refreshing the
        page usually clears it up — your saved progress is safe.
      </p>
      <button
        type="button"
        onClick={() => {
          // Full reload: simpler and more reliable than calling
          // `resetError` because most render crashes are caused by
          // stale module state that survives a re-render but not a
          // page load.
          if (typeof window !== 'undefined') {
            window.location.reload();
          }
        }}
        style={{
          padding: '0.625rem 1.25rem',
          fontSize: '1rem',
          cursor: 'pointer',
          border: '1px solid currentColor',
          borderRadius: '0.375rem',
          background: 'transparent',
        }}
      >
        Refresh the page
      </button>
    </div>
  );
}

const fallbackRender: FallbackRender = (errorData) => (
  <SentryErrorFallback
    error={errorData.error}
    componentStack={errorData.componentStack ?? ''}
    eventId={errorData.eventId ?? ''}
    resetError={errorData.resetError}
  />
);

/**
 * Wraps children in a Sentry-aware error boundary. Use as the
 * top-level wrapper in `main.tsx`. When `VITE_SENTRY_DSN` is
 * unset, Sentry is a no-op but the boundary still works (the
 * error is caught and the fallback renders) — the only difference
 * is the report doesn't leave the browser.
 */
export function AppErrorBoundary(props: {
  children: ReactNode;
}): JSX.Element {
  return (
    <ErrorBoundary fallback={fallbackRender}>{props.children}</ErrorBoundary>
  );
}
